/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { Suspense, useEffect, useRef, useState } from 'react';
import root from 'window-or-global';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import { useLabel } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import type { ReduxState } from '@/types';
import { useRouter } from 'next/router';
import azCommonStyles from '@/theme/globals.module.scss';
import ErrorComponent from '@/components/AZCustomComponent/ErrorComponent';
import { Grid } from '@/components/Grid';
import { HeadTemplate } from '@/components/PageTemplates/Head';
import { SearchPageTemplate } from '../SearchPageTemplate/SearchPageTemplate';
import TwoColumnSkeleton from '@/components/Skeletons/TwoColumn';
import styles from '../styles.module.scss';
import { useDispatch } from '@/hooks/redux/useDispatch';
import { useSearchPartTypes } from '../../api/getSearchPartTypes';
import { useSearchProductData } from '../../api/getSearchProductData';
import { useProductList } from '@/features/shelf/context/useProductList';
import { useProductListView } from '@/features/shelf/context/useProductListView';
import { MiniCartModal } from '@/components/Cart/MiniCartModal';
import { routePaths } from '@/constants/routePaths';
import { useDeviceType } from '@/utils/useDeviceType';
import { removeDataPart, setProductFilterEvents } from '@/actions/shelf/shelf';
import { SearchNoResults } from './SearchNoResults';
import Title from '@/components/AZCustomComponent/Title';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { type CertonaRecommendation, type AddCertonaItemObjectType } from '@/features/certona';
import { callToCertona } from '@/features/certona/api/callToCertona';
import { certonaPageType } from '@/constants/certonaPageType';
import Hidden from '@/components/Hidden';
import { parseUrl, sanitizeUrlWithParamList } from '@/utils/urlHelpers';
import { useDealDrawer } from '@/features/deals/hooks/useDealDrawer';
import dynamic from 'next/dynamic';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import usePrevious from '@/utils/usePrevious';
import { deleteFromHistoryStateOptions } from '@/features/common/utils/deleteFromHistoryStateOptions';
import { historyStateOptionAttributes } from '@/constants/historyStateOption';
import { VisualCategoryFilters } from '../VisualCategoryFilters';
import { View } from '@az/starc-ui';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useFeatureFlag } from '@/features/globalConfig';
import { getProductSkuDetailsKey } from '@/features/product/api/getProductSkuDetails';
import { countryCodes, defaultStoreUS } from '@/constants/locale';
import { useQueryClient } from '@tanstack/react-query';
import ShelfSkeleton from '@/components/Skeletons/ShelfSkeleton';
import { TrapFocus } from '@/components/Dialog/Modal/TrapFocus';
import { MiniCartFlyoutComp } from '@/features/miniCartFlyout';
import { selectOrderDetailsFromState } from '@/constants/selectors';
import { Drawer } from '@/components/Drawer/Drawer';
import { acceptableShelfQueryParams } from '@/constants/queryParams';
import useLatestCallback from 'use-latest-callback';
import type { HandleFilterType } from '@/types/filterTypes';
import { useIs24ProductViewEnabled } from '@/hooks/useIs24ProductViewEnabled';
import { sessionStorage } from '@/utils/sessionStorage';
import { usePageType } from '@/hooks/usePageType';
import { pageTypes as pageTypesConstants } from '@/constants/page';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import PartTypesViewTopSection from '../PartTypesView/TopSection';
import { useSearchAnalytics } from '../../lib/hooks/useSearchAnalytics';

const LazyMiniCartFlyoutComp = dynamic(
  () => import('@/features/miniCartFlyout').then((mod) => mod.MiniCartFlyoutComp),
  {
    loading: () => <ProgressModal noScroll />,
  }
);

const FilterMobileDrawer = dynamic(
  () => import('@/components/Filter/FilterMobileDrawer').then((mod) => mod.FilterMobileDrawer),
  {
    loading: () => <ProgressModal noScroll />,
  }
);

export const SearchResultsDefault = ({
  analyticsCalled,
  analyticsEventType,
  setAnalyticsCalled,
  certonaRecommendations,
  viewingPartTypes,
  setViewingPartTypes,
}: {
  analyticsCalled: boolean;
  analyticsEventType: string | undefined;
  setAnalyticsCalled: (called: boolean) => void;
  certonaRecommendations: CertonaRecommendation[];
  viewingPartTypes: boolean;
  setViewingPartTypes: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const locale = useLocale();
  const deviceType = useDeviceType();
  const miniCartModalRef = useRef<HTMLDivElement>(null);
  const lazyMiniCartModalRef = useRef<HTMLDivElement>(null);
  const [showMiniCartModal, setShowMiniCartModal] = useState(false);
  const [certonaCalled, setCertonaCalled] = useState(false);
  const [certonaRecommendationAddedToCart, setCertonaRecommendationAddedToCart] = useState(false);
  const [showCertonaMiniCartFlyoutModal, setShowCertonaMiniCartFlyoutModal] = useState(false);
  const [addedCertonaItemObject, setAddedCertonaItemObject] =
    useState<AddCertonaItemObjectType | null>(null);
  const isFetchingLocation = useSelector(({ appData }: ReduxState) => appData.isFetchingLocation);
  const { query } = parseUrl(router.asPath);
  const { cartSuccess } = useSelector(({ shelf }: ReduxState) => shelf);
  const queryDisplayLabel =
    typeof router.query.searchText === 'string' ? router.query.searchText : '';
  const { renderDealDrawer, handleDealClick } = useDealDrawer();
  const preferredVehicle = usePreferredVehicle();
  const vehicleName = preferredVehicle?.vehicleName;
  const vehicleId = preferredVehicle?.catalogVehicleId;
  const { data: storeDetails } = useStoreDetailsData();
  const queryClient = useQueryClient();
  const { data: headerData } = useHeaderData();
  const miniCartSuccessNote = headerData?.miniCartSuccessNote;
  const miniCartMap = headerData?.miniCartMap;
  const [miniCartFlyoutVisible, setMiniCartFlyoutVisible] = useState(false);
  const [showMiniCartFlyoutModal, setShowMiniCartFlyoutModal] = useState(false);
  const orderDetailsFromState = useSelector(selectOrderDetailsFromState);
  const { pageType } = usePageType();
  const isShelfOrSearchPage =
    pageType === pageTypesConstants.shelf ||
    pageType === pageTypesConstants.ProductShelf ||
    pageType === pageTypesConstants.search;
  const isVisualFiltersEnabled = useMonetateDecisionFlag('visualPartTypes');
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));
  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const isUS = locale === countryCodes.us;
  const useNewShelfDesignMobile =
    useMonetateDecisionFlag('useNewShelfDesignMobile') && isMobile && isUS && isShelfOrSearchPage;
  const desktopMiniCartFlyoutEnabled =
    useFeatureFlag('IS_DESKTOP_MINI_CART_FLYOUT_ENABLED') === 'true';
  const mobileMiniCartFlyoutEnabled =
    useFeatureFlag('IS_MOBILE_MINI_CART_FLYOUT_ENABLED') === 'true';
  const tabletMiniCartFlyoutEnabled =
    useFeatureFlag('IS_TABLET_MINI_CART_FLYOUT_ENABLED') === 'true';
  const enable24ProductView = useIs24ProductViewEnabled();
  const isNewDealsApiEnabled = useFeatureFlag('IS_NEW_DEALS_API_ENABLED') === 'true';
  const isLoadMoreEnabled = useMonetateDecisionFlag('loadMoreEnabled');
  const isMiniCartFlyoutEnabled =
    (isDesktop && desktopMiniCartFlyoutEnabled) ||
    (isMobile && mobileMiniCartFlyoutEnabled) ||
    (isTablet && tabletMiniCartFlyoutEnabled);
  const toggleMiniCartFlyout = () => {
    setMiniCartFlyoutVisible(() => !miniCartFlyoutVisible);
  };
  const dispatchSetProductFilterEvents = (
    filterName: string,
    filterId: string,
    checked: boolean,
    quickFilter?: boolean
  ) => dispatch(setProductFilterEvents(filterName, filterId, checked, quickFilter));
  const isSearchPageCertonaAddToCartEnabled =
    useFeatureFlag('IS_SEARCH_PAGE_CERTONA_ADD_TO_CART_ENABLED') === 'true';

  const {
    data: searchPartTypesData,
    isError: searchPartTypesError,
    isLoading: searchPartTypesLoading,
    isSuccess: searchPartTypesSuccess,
    fetchStatus: searchPartTypesFetchStatus,
  } = useSearchPartTypes({ enabled: viewingPartTypes, viewingPartTypes });
  const {
    data: searchProductData,
    isError: searchProductError,
    isLoading: searchProductLoading,
    isSuccess: searchProductSuccess,
    fetchStatus: searchProductFetchStatus,
  } = useSearchProductData({ enabled: !viewingPartTypes });
  const {
    data: productListData,
    isSuccess: productListSuccess,
    productSkuIsFetching: isProductSkuFetching,
    productSkuIsLoading,
    secondaryProductSkuIsFetching,
    loadMoreClicked,
    setShouldFetchSecondaryProductSkus,
  } = useProductList();
  const prevSecondaryProductSkuIsFetching = usePrevious(secondaryProductSkuIsFetching);

  const { isMobileFilterOpen } = useProductListView();

  const parts = productListData?.shelfParts;
  const isABot = deviceType === 'bot';
  const isIdle = searchProductFetchStatus === 'idle' && searchPartTypesFetchStatus === 'idle';
  const isError = searchProductError || searchPartTypesError;
  const isProductDataLoaded = searchProductSuccess && productListSuccess;
  const isLoading = viewingPartTypes
    ? searchPartTypesFetchStatus === 'fetching' || searchPartTypesLoading || !searchPartTypesSuccess
    : searchProductFetchStatus === 'fetching' || searchProductLoading || !isProductDataLoaded;

  useEffect(() => {
    return () => {
      dispatch(removeDataPart());
    };
  }, [dispatch]);

  useEffect(() => {
    if (cartSuccess && miniCartSuccessNote) {
      setShowMiniCartModal(true);
      setMiniCartFlyoutVisible(true);
      setShowMiniCartFlyoutModal(true);
    }
  }, [cartSuccess, miniCartSuccessNote]);

  useEffect(() => {
    return () => {
      setMiniCartFlyoutVisible(false);
      setShowMiniCartFlyoutModal(false);
    };
  }, []);

  const resultLabel = useLabel('label_Search_Results_Results');

  const closeStoreModal = () => {
    dispatch(removeDataPart());
    setShowMiniCartModal(false);
    setMiniCartFlyoutVisible(false);
    setShowMiniCartFlyoutModal(false);
  };

  const submitToCart = () => {
    void router.push(routePaths.cart);
  };

  const renderMiniCart = () => {
    const addToCartItem =
      isABot && parts
        ? parts[0]
        : parts?.find(
            (item) =>
              String(item?.skuPricingAndAvailability?.productId) === cartSuccess?.productId &&
              String(item?.skuNumber) === cartSuccess?.skuId
          );

    const productImage = addToCartItem?.productImageUrl || '';
    const productName = {
      productBrand: addToCartItem?.brand || '',
      productPart: addToCartItem?.name || '',
      quickNote: addToCartItem?.quickNote || '',
    };
    const productAvailabilityInfo = {
      skuPricingAndAvailability: addToCartItem?.skuPricingAndAvailability,
      dealsInfo: addToCartItem?.dealsInfo,
      aZBadgesFlagVO: addToCartItem?.aZBadgesFlagVO,
      availabilityInfoVO: addToCartItem?.availabilityInfoVO,
    };
    const productDetails = {
      productName,
      productAvailabilityInfo,
      productPartType: addToCartItem?.originalPartTypeId,
    };

    const checkToOpenMiniCartModal =
      addToCartItem &&
      (cartSuccess || miniCartSuccessNote) &&
      (showMiniCartModal || showMiniCartFlyoutModal);

    const renderOldMiniCart = () => (
      <Hidden only={['sm', 'md']}>
        <MiniCartModal
          // @ts-expect-error fix type
          isModalOpen={global !== 'undefined' ? showMiniCartModal : false}
          image={productImage}
          successData={cartSuccess}
          productDetails={productDetails}
          closeStoreModal={closeStoreModal}
          cartUrl={submitToCart}
          productTitle={productName.productPart}
          pageName="search"
        />
      </Hidden>
    );
    const renderNewMiniCartFlyout = () => (
      <Drawer
        anchor={'right'}
        open={miniCartFlyoutVisible}
        onClose={toggleMiniCartFlyout}
        PaperProps={{ className: styles.customPaperDrawer }}
        drawerRef={miniCartModalRef}
      >
        <TrapFocus open={miniCartFlyoutVisible}>
          <div
            className={styles.miniCartFlyoutDialog}
            role="dialog"
            aria-modal="true"
            data-testid="deals-bundle-drawer-sister-store-flyout"
          >
            <MiniCartFlyoutComp
              handleClose={toggleMiniCartFlyout}
              isModalOpen={showMiniCartFlyoutModal}
              image={productImage}
              successData={cartSuccess}
              productDetails={productDetails}
              miniCartData={miniCartMap}
              closeStoreModal={closeStoreModal}
              cartUrl={submitToCart}
              productTitle={productName.productPart}
              pageName="pdp"
              orderDetailsFromState={orderDetailsFromState}
              parentRef={miniCartModalRef}
            />
          </div>
        </TrapFocus>
      </Drawer>
    );
    if (checkToOpenMiniCartModal) {
      if (isMiniCartFlyoutEnabled) {
        return renderNewMiniCartFlyout();
      } else {
        return renderOldMiniCart();
      }
    }
  };

  const toggleCertonaMiniCartFlyout = () => {
    setShowCertonaMiniCartFlyoutModal(!showCertonaMiniCartFlyoutModal);
    closeStoreModal();
  };

  useEffect(() => {
    if (!!addedCertonaItemObject) {
      setShowCertonaMiniCartFlyoutModal(true);
    }
  }, [addedCertonaItemObject]);

  // since onSuccess will be deprecated and it does not account for cached data, we must manually redirect
  useEffect(() => {
    if (searchPartTypesData?.redirectLocation || searchProductData?.redirectUrl) {
      if (searchPartTypesData?.oemPartNumber || searchProductData?.oemPartNumber) {
        sessionStorage.setItem(
          'oemPartNumber',
          searchPartTypesData?.oemPartNumber ?? searchProductData?.oemPartNumber ?? ''
        );
      }
      if (searchPartTypesData?.oemBrandName || searchProductData?.oemBrandName) {
        sessionStorage.setItem(
          'oemBrandName',
          searchPartTypesData?.oemBrandName ?? searchProductData?.oemBrandName ?? ''
        );
      }
      const { url, query: queryParams } = parseUrl(
        searchPartTypesData?.redirectLocation || searchProductData?.redirectUrl || ''
      );
      const sanitizedRedirectPath = sanitizeUrlWithParamList(
        {
          ...queryParams,
          ...(router.query.searchText && {
            searchText: router.query.searchText,
          }),
          ...{ redirectUrl: 'true' },
        },
        acceptableShelfQueryParams,
        url
      );

      void router.replace(sanitizedRedirectPath);
    }
  }, [router, searchPartTypesData, searchProductData]);

  // since the route does not change when viewing part types, this will control the state when user presses back/forward on browser
  useEffect(() => {
    const popListener = (e: PopStateEvent) => {
      const state = e?.state as { url: string } | undefined;
      const { query } = parseUrl(state?.url ?? '');
      if (viewingPartTypes && query.partTypesView !== 'true') {
        setViewingPartTypes(false);
      } else if (!viewingPartTypes && query.partTypesView === 'true') {
        setViewingPartTypes(true);
      }
    };
    window.addEventListener('popstate', popListener);
    return () => window.removeEventListener('popstate', popListener);
  }, [viewingPartTypes, setViewingPartTypes]);

  useEffect(() => {
    if (
      root.history.state &&
      'options' in root.history.state &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      'latestSelectedPageYOffset' in root.history.state.options
    ) {
      if (
        enable24ProductView &&
        setShouldFetchSecondaryProductSkus &&
        isLoadMoreEnabled &&
        !loadMoreClicked
      ) {
        setShouldFetchSecondaryProductSkus(true);
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const latestSelectedPageYOffset = root.history.state.options
        .latestSelectedPageYOffset as string;

      if (
        parseInt(latestSelectedPageYOffset) > 0 &&
        productListSuccess &&
        !isProductSkuFetching &&
        !productSkuIsLoading &&
        (!enable24ProductView ||
          (prevSecondaryProductSkuIsFetching && !secondaryProductSkuIsFetching))
      ) {
        root.scroll({
          top: parseInt(latestSelectedPageYOffset),
        });
        deleteFromHistoryStateOptions(historyStateOptionAttributes.latestSelectedPageYOffset);
      }
    }
  }, [
    loadMoreClicked,
    isLoadMoreEnabled,
    productListSuccess,
    isProductSkuFetching,
    enable24ProductView,
    setShouldFetchSecondaryProductSkus,
    secondaryProductSkuIsFetching,
    prevSecondaryProductSkuIsFetching,
    productSkuIsLoading,
  ]);

  useSearchAnalytics({
    analyticsCalled,
    analyticsEventType,
    viewingPartTypes,
    isLoadMoreEnabled,
    isMobile,
    setAnalyticsCalled,
    setViewingPartTypes,
  });

  useEffect(() => {
    if (!!searchProductData?.lwTestExperience && searchProductData.redirectUrl) {
      sessionStorage.setItem('lwTestExperience', searchProductData.lwTestExperience);
    }

    if ((searchPartTypesSuccess || isProductDataLoaded) && !certonaCalled) {
      !searchProductData?.records?.length && !searchPartTypesData?.records?.length
        ? void callToCertona({ certonaType: certonaPageType.searchResultPage })
        : null;
      setCertonaCalled(true);
    }
  }, [
    queryDisplayLabel,
    certonaCalled,
    searchPartTypesSuccess,
    isProductDataLoaded,
    searchPartTypesData,
    searchProductData,
  ]);

  // Copies changes made to shelf on SB-1894
  useEffect(() => {
    root.scrollTo(0, 0);
    return () => {
      const skuIds =
        searchProductData?.records?.map((part) => {
          return part.skuNumber.toString();
        }) ?? [];
      const storeNumber =
        locale === countryCodes.us
          ? storeDetails?.storeNumber || defaultStoreUS
          : storeDetails?.storeNumber;
      queryClient.removeQueries(
        getProductSkuDetailsKey({
          skuIds,
          vehicleId,
          storeNumber,
          isNewDealsApiEnabled,
        })
      );
    };
    // This should only be run on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = useLatestCallback((filter: HandleFilterType) => {
    const { checked, displayName, quickFilter, refinement, selectionType } = filter;
    const { label, properties } = refinement;
    const isQuickFilter = !!quickFilter && !isDesktop;
    const filterName = `${selectionType}: ${displayName}: ${label}`;
    const filterId = properties.filters;

    dispatchSetProductFilterEvents(filterName, filterId, Boolean(checked), isQuickFilter);
  });

  const rppOptionsData = enable24ProductView && !viewingPartTypes ? '24,36,48' : '12,24,36,48';

  // Add margin between topnav and search results unless VisualCategoryFilters are displayed
  const showContainerMargin =
    !isLoading &&
    !viewingPartTypes &&
    !useNewShelfDesignMobile &&
    (!isMobile || (isMobile && searchProductData?.partTypeRecords?.length === 0));

  return (
    <div
      className={cx(styles.tabletNoPadding, {
        [azCommonStyles.mainContentContainer]: !isMobile || viewingPartTypes,
      })}
    >
      {isMobileFilterOpen && (
        <FilterMobileDrawer
          staticFilterData={
            viewingPartTypes
              ? searchPartTypesData?.staticNavigation ?? []
              : searchProductData?.staticNavigation ?? []
          }
          pageType="searchResults"
          showSpinner={isLoading && !isIdle}
          onFilterSelect={handleFilter}
        />
      )}
      {isProductDataLoaded && renderMiniCart()}
      {(isLoading ||
        isFetchingLocation ||
        searchProductData?.redirectUrl ||
        searchPartTypesData?.redirectLocation) &&
      (isLoadMoreEnabled ? !loadMoreClicked : true) ? (
        !viewingPartTypes ? (
          <View padding={[10, 0]}>
            <ShelfSkeleton />
          </View>
        ) : (
          <TwoColumnSkeleton />
        )
      ) : searchPartTypesSuccess || isProductDataLoaded ? (
        <div
          className={cx({
            [styles.containerMargin]: showContainerMargin,
            [styles.mobileMargin]:
              !searchProductData?.records?.length &&
              !searchPartTypesData?.records?.length &&
              (isMobile || isTablet),
          })}
          data-testid="search-results-default"
        >
          <HeadTemplate
            content={[{ '@type': 'SearchHead_SEOMetaTag', metaTagName: 'og:url(Facebook)' }]}
          />
          {renderDealDrawer()}
          {viewingPartTypes && searchPartTypesData ? (
            <PartTypesViewTopSection
              setViewingPartTypes={setViewingPartTypes}
              setAnalyticsCalled={setAnalyticsCalled}
            />
          ) : (
            isVisualFiltersEnabled &&
            isProductDataLoaded && (
              <>
                {(searchProductData?.partTypeRecords?.length ?? 0) > 1 &&
                  (!query.facet || query.facet === 'vehicle_fitment:VehicleSpecific Products') && (
                    <View
                      padding={
                        useNewShelfDesignMobile
                          ? !preferredVehicle
                            ? [1, 0, 2, 0]
                            : [1, 0, 3, 0]
                          : { s: [0, 4, 3.5, 4] }
                      }
                    >
                      <VisualCategoryFilters
                        viewingPartTypes={viewingPartTypes}
                        setViewingPartTypes={(val: boolean) => {
                          setViewingPartTypes(val);
                          setAnalyticsCalled(false);
                        }}
                      />
                    </View>
                  )}
              </>
            )
          )}
          {((!viewingPartTypes && !searchProductData?.records?.length) ||
            (viewingPartTypes && !searchPartTypesData?.records?.length)) && (
            <Grid container spacing={0}>
              <Title
                firstText={`0 ${resultLabel}`}
                firstTextClass={cx(
                  azCommonStyles['az-title-2-regular'],
                  styles.noResultsPageHeading
                )}
                usesWrapperTags
              />
            </Grid>
          )}
          <View className={styles.searchContentContainer}>
            <View className={styles.searchLeftContainer}>
              <div
                className={cx({
                  [styles.shelfNav]: !useNewShelfDesignMobile || viewingPartTypes,
                  [styles.stickyNav]:
                    searchProductData?.records?.length || searchPartTypesData?.records?.length,
                })}
              >
                {searchProductData?.records?.length || searchPartTypesData?.records?.length ? (
                  <Hidden mdDown={!viewingPartTypes}>
                    <SearchPageTemplate
                      viewingPartTypes={viewingPartTypes}
                      onFilterSelect={handleFilter}
                      content={[{ '@type': 'SearchLeftNav_FilterNav' }]}
                    />
                  </Hidden>
                ) : (
                  <SearchPageTemplate
                    onFilterSelect={handleFilter}
                    viewingPartTypes={viewingPartTypes}
                    content={[{ '@type': 'SearchLeftNav_CustomFallback' }]}
                  />
                )}
              </div>
            </View>
            <View className={styles.searchRightContainer}>
              {searchProductData?.records?.length || searchPartTypesData?.records?.length ? (
                <SearchPageTemplate
                  content={[
                    {
                      '@type': 'SearchPageBody_YMME',
                      desktop: true,
                      layoutCode: 'Thin',
                      mobile: true,
                      name: 'YMME Content Block',
                      tablet: true,
                    },
                    { '@type': 'SearchPageBody_AZCombinedSearchResults' },
                  ]}
                  queryDisplayLabel={queryDisplayLabel}
                  viewingPartTypes={viewingPartTypes}
                  disableRecordsPerPage={
                    !viewingPartTypes && enable24ProductView
                      ? (searchProductData?.total ?? 36) < 36
                      : undefined
                  }
                  onFilterSelect={handleFilter}
                  handleDealClick={handleDealClick}
                  rppOptionsData={rppOptionsData}
                />
              ) : (
                <>
                  <SearchPageTemplate
                    viewingPartTypes={viewingPartTypes}
                    content={[
                      {
                        '@type': 'SearchPageBody_YMME',
                        desktop: true,
                        layoutCode: 'Thin',
                        mobile: true,
                        name: 'YMME Content Block',
                        tablet: true,
                      },
                    ]}
                    queryDisplayLabel={queryDisplayLabel}
                    rppOptionsData={rppOptionsData}
                  />
                  <SearchNoResults
                    vehicleName={vehicleName}
                    queryDisplayLabel={queryDisplayLabel}
                  />
                </>
              )}
            </View>
          </View>
          {((!viewingPartTypes && (searchProductData?.records?.length ?? 0) > 0) ||
            (viewingPartTypes && (searchPartTypesData?.records?.length ?? 0) > 0)) && (
            <div className={styles.bottomRecommendations}>
              <SearchPageTemplate
                content={[
                  {
                    tablet: true,
                    desktop: true,
                    '@type': 'ShelfResultBottom_Certona',
                    themeCode: 'white',
                    name: 'Certona Content Block',
                    mobile: true,
                    containerID: 'nosearch_rr',
                  },
                ]}
                viewingPartTypes={viewingPartTypes}
                certonaRecommendations={certonaRecommendations}
                certonaOptions={{
                  containerType: isMobile ? 'horizontal_stacked' : 'large_scrolled',
                  onCertonaAddToCartClick: () => setCertonaRecommendationAddedToCart(true),
                  setAddedCertonaItemObject,
                  onlyShowAvailableProductRecommendations: isSearchPageCertonaAddToCartEnabled,
                }}
              />
            </div>
          )}
          {((!viewingPartTypes && !searchProductData?.records?.length) ||
            (viewingPartTypes && !searchPartTypesData?.records?.length)) && (
            <SearchPageTemplate
              certonaRecommendations={certonaRecommendations}
              content={[
                {
                  tablet: true,
                  desktop: true,
                  '@type': 'ShelfResultBottom_Certona',
                  themeCode: 'white',
                  name: 'Certona Content Block',
                  mobile: true,
                  containerID: 'nosearch_rr',
                },
              ]}
              viewingPartTypes={viewingPartTypes}
              queryDisplayLabel={queryDisplayLabel}
              rppOptionsData={rppOptionsData}
            />
          )}
        </div>
      ) : null}
      {isError ? <ErrorComponent /> : null}
      <Drawer
        data-testid="mini-cart-flyout"
        anchor="right"
        open={showCertonaMiniCartFlyoutModal}
        onClose={toggleCertonaMiniCartFlyout}
        PaperProps={{ className: styles.customPaperDrawer }}
        drawerRef={lazyMiniCartModalRef}
      >
        <TrapFocus open={showCertonaMiniCartFlyoutModal}>
          <div className={styles.miniCartFlyoutDialog} role="dialog" aria-modal="true">
            <Suspense>
              {showCertonaMiniCartFlyoutModal && addedCertonaItemObject && (
                <LazyMiniCartFlyoutComp
                  handleClose={toggleCertonaMiniCartFlyout}
                  isModalOpen={showCertonaMiniCartFlyoutModal}
                  image={addedCertonaItemObject.productImage}
                  successData={addedCertonaItemObject.cartSuccessData}
                  productDetails={{
                    productName: addedCertonaItemObject.productDetails.productName,
                    certonaItemAddedTocart:
                      certonaRecommendationAddedToCart && isSearchPageCertonaAddToCartEnabled,
                    productPartType: addedCertonaItemObject.productDetails.partType,
                    originalPartTypeId: addedCertonaItemObject.productDetails.partType,
                    productId: addedCertonaItemObject.productDetails.productId,
                    productAvailabilityInfo: {
                      aZBadgesFlagVO: null,
                      dealsInfo: undefined,
                      skuPricingAndAvailability: undefined,
                    },
                  }}
                  miniCartData={miniCartMap}
                  closeStoreModal={closeStoreModal}
                  cartUrl={addedCertonaItemObject.submitToCart}
                  productTitle={addedCertonaItemObject.productTitle}
                  pageName={addedCertonaItemObject.pageName}
                  orderDetailsFromState={orderDetailsFromState}
                  skuId={addedCertonaItemObject.productDetails.skuId}
                  parentRef={lazyMiniCartModalRef}
                />
              )}
            </Suspense>
          </div>
        </TrapFocus>
      </Drawer>
    </div>
  );
};
